import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Group, Text } from '@mantine/core';
import { IconEye } from '@tabler/icons-react';
import { contextsTestIds } from '@contexts/testids';
import useAuth from './useAuth';
import styles from './ImpersonationBanner.module.css';

/**
 * TV1-1860 — persistent layout-level banner shown while an admin is operating
 * the web app under an impersonated user's session. Mounted in `App.tsx` above
 * `<RouterProvider>` so it appears on every route (including `/auth`).
 *
 * Renders `null` when `!isImpersonating`, so normal users get zero DOM impact.
 *
 * The "Stop Impersonating" button calls `useAuth().stopImpersonating()`, which
 * fires the BA-backed `StopImpersonatingAuthUser` aggregate command, clears the
 * sessionStorage entry, and closes the tab (with a `/auth` fallback for tabs
 * not opened via `window.open`).
 */
export function ImpersonationBanner() {
  const { t } = useTranslation( 'authentication' );
  const auth = useAuth();
  // Local UI loading state — `stopImpersonating()` ends the React tree by
  // closing the tab or redirecting, so we do not reset it in a finally
  // block: the next paint either is gone (tab closed) or is the /auth
  // landing where this banner does not render. Resetting would warn on an
  // unmounted tree.
  const [ isLoading, setIsLoading ] = useState<boolean>( false );

  if ( !auth ) return null;
  const { isImpersonating, impersonatedUser, stopImpersonating } = auth;

  if ( !isImpersonating || !impersonatedUser ) return null;

  const handleStop = async () => {
    if ( isLoading ) return;
    setIsLoading( true );
    await stopImpersonating();
  };

  return (
    <Group
      justify="space-between"
      align="center"
      px="md"
      py="xs"
      gap="sm"
      data-testid={contextsTestIds.impersonationBanner}
      className={styles.banner}
    >
      <Group gap="xs" align="center" wrap="nowrap">
        <IconEye size={16} />
        <Text fz="sm" data-testid={contextsTestIds.impersonationBannerUserInfo}>
          {t( 'impersonation.banner.viewing_as', {
            name: impersonatedUser.name,
            email: impersonatedUser.email
          })}
        </Text>
      </Group>
      <Button
        variant="light"
        color="yellow"
        size="xs"
        loading={isLoading}
        onClick={handleStop}
        data-testid={contextsTestIds.impersonationBannerStopButton}
      >
        {isLoading ? t( 'impersonation.banner.stopping' ) : t( 'impersonation.banner.stop_button' )}
      </Button>
    </Group>
  );
}

export default ImpersonationBanner;
